import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CuratorGroup } from '@/api/curatorGroups';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { Modal } from '@/components/ui/Modal';
import { MAdd, MBlock, MRow, MSection, MTag } from '@/components/mobile/MobileKit';
import { validGroupName } from '@/lib/curatorGroupSelection';

/** Вместимость по умолчанию — как у админки: ориентир, а не запрет (договор §10). */
const DEFAULT_CAPACITY = 20;

/**
 * Группы курса и создание новой (D40, договор §2 и §3).
 *
 * ⚠️ ВМЕСТИМОСТЬ НЕ БЛОКИРУЕТ. Сервер её не проверяет вовсе; на экране
 * превышение только подсвечено. Запрещать здесь значило бы придумать правило,
 * которого нет ни в базе, ни в договоре.
 *
 * Куратор не переименовывает и не удаляет группы и не добавляет в них других
 * кураторов — это админка (§10).
 */
export function GroupsCard({
  groups,
  loading,
  error,
  created,
  creating,
  onCreate,
  phone = false,
}: {
  groups: CuratorGroup[];
  loading: boolean;
  error: string | null;
  created: boolean;
  creating: boolean;
  onCreate: (name: string, capacity: number) => void;
  /** Телефон (§4.9): группы строками, «Создать группу» — окном во весь экран по «+». */
  phone?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState(DEFAULT_CAPACITY);
  const [formOpen, setFormOpen] = useState(false);

  const tooLong = name.trim().length > 120;
  const canCreate = validGroupName(name) && !creating;

  // Группа создана — окно закрывается, поле очищается для следующей.
  useEffect(() => {
    if (created) {
      setFormOpen(false);
      setName('');
    }
  }, [created]);

  if (phone) {
    return (
      <div className="space-y-2">
        <MSection
          title={t('curatorGroups.groupsTitle')}
          right={<MAdd label={t('curatorGroups.createTitle')} onClick={() => setFormOpen(true)} />}
        />
        {loading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : !groups.length ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('curatorGroups.groupsEmpty')}</p>
        ) : (
          <MBlock>
            {groups.map((g, i) => {
              const over = g.member_count > g.capacity;
              return (
                <MRow
                  key={g.id}
                  wrap
                  title={g.name}
                  meta={
                    g.curators.length
                      ? g.curators.map((c) => c.name).filter(Boolean).join(', ')
                      : t('curatorGroups.groupNoCurators')
                  }
                  aside={
                    <span className="flex flex-none flex-col items-end gap-1">
                      {/* Превышение только подсвечено — вместимость не блокирует (договор §10). */}
                      <MTag tone={over ? 'warm' : 'muted'}>
                        {t('curatorGroups.groupMembers', { count: g.member_count, capacity: g.capacity })}
                      </MTag>
                      {g.mine && <MTag>{t('curatorGroups.groupMine')}</MTag>}
                    </span>
                  }
                  last={i === groups.length - 1}
                />
              );
            })}
          </MBlock>
        )}
        {created && !error && <p className="px-0.5 text-[13px] text-success">{t('curatorGroups.groupCreated')}</p>}
        <Modal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={t('curatorGroups.createTitle')}
          phoneFull
          footer={
            <div className="grid">
              <Button disabled={!canCreate} loading={creating} onClick={() => onCreate(name.trim(), capacity)}>
                {t('curatorGroups.createButton')}
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <Input
              data-group-name
              placeholder={t('curatorGroups.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              data-group-capacity
              type="number"
              min={1}
              label={t('curatorGroups.capacityLabel')}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value) || DEFAULT_CAPACITY)}
            />
            {tooLong && <p className="text-sm text-error">{t('curatorGroups.nameTooLong')}</p>}
            {error && <p className="text-sm text-error">{error}</p>}
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <Card className="mb-4">
      <CardTitle>{t('curatorGroups.groupsTitle')}</CardTitle>

      {loading ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : !groups.length ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('curatorGroups.groupsEmpty')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {groups.map((g) => {
            const over = g.member_count > g.capacity;
            return (
              <li key={g.id} className="flex flex-wrap items-center gap-2 rounded-card bg-surface-container-lowest px-3 py-2">
                <span className="font-semibold text-on-surface">{g.name}</span>
                <Pill variant={over ? 'warning' : 'neutral'}>
                  {t('curatorGroups.groupMembers', { count: g.member_count, capacity: g.capacity })}
                </Pill>
                {g.mine && <Pill variant="success">{t('curatorGroups.groupMine')}</Pill>}
                <span className="text-xs text-on-surface-variant">
                  {g.curators.length
                    ? g.curators.map((c) => c.name).filter(Boolean).join(', ')
                    : t('curatorGroups.groupNoCurators')}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 border-t border-outline-variant/40 pt-4">
        <p className="mb-2 text-sm font-semibold text-on-surface">{t('curatorGroups.createTitle')}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <Input
            data-group-name
            className="flex-1"
            placeholder={t('curatorGroups.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            data-group-capacity
            type="number"
            min={1}
            className="sm:w-32"
            label={t('curatorGroups.capacityLabel')}
            value={capacity}
            onChange={(e) => setCapacity(Number(e.target.value) || DEFAULT_CAPACITY)}
          />
          <Button disabled={!canCreate} loading={creating} onClick={() => onCreate(name.trim(), capacity)}>
            {t('curatorGroups.createButton')}
          </Button>
        </div>
        {tooLong && <p className="mt-2 text-sm text-error">{t('curatorGroups.nameTooLong')}</p>}
        {error && <p className="mt-2 text-sm text-error">{error}</p>}
        {created && !error && <p className="mt-2 text-sm text-success">{t('curatorGroups.groupCreated')}</p>}
      </div>
    </Card>
  );
}
